import { boolean, index, pgEnum, pgTable, text, timestamp, uniqueIndex } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import type { z } from 'zod';
import { generateId, recognizableId, timestamps } from './shared';
import { organizationTable } from './organization';

// Define enum values as constants for reuse in validation schemas
export const USER_ROLES_VALUES = [
  'CreateCompany',
  'Developer',
  'Sales',
  'Ops',
  'Treasurer',
  'FinanceRO',
] as const;
export const USER_STATUS_VALUES = ['Pending', 'Approved', 'Rejected'] as const;

export const UserRoles = pgEnum('UserRoles', USER_ROLES_VALUES);
export const UserStatus = pgEnum('UserStatus', USER_STATUS_VALUES);

// Export types for the enum values
export type UserRole = (typeof USER_ROLES_VALUES)[number];
export type UserStatusValue = (typeof USER_STATUS_VALUES)[number];

export const userTable = pgTable(
  'users',
  {
    id: recognizableId('id', { mode: 'text' })
      .primaryKey()
      .$defaultFn(() => generateId('fu', 21)),
    atid: text().unique(),
    Name: text().notNull(),
    firstName: text(), // nullable - null means profile incomplete (existing users)
    lastName: text(), // nullable - null means profile incomplete (existing users)
    email: text().notNull(),
    kindeId: text().unique(),
    Roles: UserRoles().array().notNull().default([]),
    userStatus: UserStatus('userStatus').notNull().default('Pending'),
    ApprovalDate: timestamp(),
    organizationId: recognizableId('organizationId', { mode: 'text' }).references(
      () => organizationTable.id,
      { onDelete: 'set null' },
    ),
    // Per-user opt-in for the developer surface (the My Org → API sidebar item).
    // A DISPLAY preference, not an entitlement — the `feature_api_ui` allow-list
    // (src/lib/feature-flags/api-ui-access.ts) remains the sole access gate, and
    // the /owner/organization/[orgId]/api route is deliberately NOT gated on this
    // column so turning it off never 404s a bookmarked link. Defaults false:
    // ordinary merchants never see developer surface area.
    showDeveloperUi: boolean('showDeveloperUi').notNull().default(false),
    ...timestamps,
  },
  (table) => [
    index('idx_user_email').on(table.email),
    index('idx_user_name').on(table.atid),
    index('idx_user_kindeid').on(table.kindeId),
    index('idx_user_organization_id').on(table.organizationId),
    uniqueIndex('users_contact_email_org')
      .on(table.email, table.organizationId)
      .where(sql`"kindeId" IS NULL`),
  ],
);

export const userSchema = createSelectSchema(userTable);
export const userInsertSchema = createInsertSchema(userTable);
export const userUpdateSchema = createInsertSchema(userTable).partial();
export type UserSelect = z.infer<typeof userSchema>;
export type UserInsert = z.infer<typeof userInsertSchema>;
export type UserUpdate = z.infer<typeof userUpdateSchema>;
export type UserCreate = Pick<
  UserInsert,
  'Name' | 'email' | 'kindeId' | 'firstName' | 'lastName' | 'userStatus' | 'ApprovalDate' | 'Roles'
>;
